// src/core-app/lib/obsEtlToMatrix/derived-field-expr.js
//------------------------------------------------------------------------------
//
// Builds DerivedFieldExpr objects (the new format) from workbench tree nodes.
//
// Each derived field is a self-contained expression tree that etl-rs can
// resolve without predicting column names or looking up the rest of the request.
//

/**
 * Build InputExpr::Aggregation(s) from a measurement node's data.
 *
 * When components are in "series" mode (each value becomes a separate column),
 * this expands into multiple aggregation inputs — one per selected value.
 * This mirrors how the meaExpressions expand in the base matrix request.
 *
 * @param {Object} nodeData - height-4 measurement node's .data.value
 * @returns {Array<{input: Object, label: string}>} array of {input, label} pairs
 */
export function aggregationInputsFromNode(nodeData) {
  const source = nodeData.measurementType;

  // Separate components into fixed filters vs series expansions
  const fixedFilters = [];
  const seriesComponents = [];

  Object.values(nodeData.values || {})
    .filter((comp) => comp.request !== false)
    .forEach((comp) => {
      // Detect component type by tag or by presence of span values in the values map
      const isSpan = comp.tag === 'spanValues' || hasSpanValues(comp);

      if (isSpan) {
        // Time span — extract range from values entries
        Object.values(comp.values || {})
          .filter((v) => v.request !== false && v.value)
          .forEach((v) => {
            const span = v.value;
            fixedFilters.push({
              type: 'spanRange',
              field: comp.componentName,
              start: span.rangeStart,
              end: span.rangeStart + span.rangeLength - 1,
            });
          });
      } else if (comp.selectionModel) {
        // Categorical component
        const isSeries = comp.selectionModel.computationType === 'SERIES';

        // Get selected values from the selectionModel
        const selected = Object.entries(comp.selectionModel.values || {})
          .filter(([key, val]) => key !== '__ALL__' && val.request === true)
          .map(([, val]) => val.value);

        if (selected.length === 0) return;

        if (isSeries) {
          seriesComponents.push({
            field: comp.componentName,
            values: selected,
          });
        } else {
          fixedFilters.push({
            type: 'textEq',
            field: comp.componentName,
            values: selected,
          });
        }
      }
    });

  // Build a label suffix from ALL fixed filters for uniqueness
  const filterParts = fixedFilters.map((f) => {
    if (f.type === 'spanRange') return `${f.field}::${f.start}_${f.end}`;
    if (f.type === 'textEq') return `${f.field}::${f.values.join('+')}`;
    if (f.type === 'textNeq') return `${f.field}::!${f.values.join('+')}`;
    return '';
  }).filter(Boolean);
  const labelSuffix = filterParts.length > 0 ? `.${filterParts.join('.')}` : '';

  // If no series components, return a single aggregation
  if (seriesComponents.length === 0) {
    return [{
      input: { kind: 'aggregation', source, filters: fixedFilters, reducer: 'SUM' },
      label: `${source}${labelSuffix}`,
    }];
  }

  // Expand series: one aggregation per combination of series values
  // For simplicity, handle the common case of one series component
  // (multiple series components would need a cartesian product)
  const results = [];
  const [series] = seriesComponents;

  for (const value of series.values) {
    const filters = [
      ...fixedFilters,
      { type: 'textEq', field: series.field, values: [value] },
    ];
    results.push({
      input: { kind: 'aggregation', source, filters, reducer: 'SUM' },
      label: `${source}.${series.field}::${value}${labelSuffix}`,
    });
  }

  return results;
}

/** Check if a component has span values in its values map */
function hasSpanValues(comp) {
  return Object.values(comp.values || {}).some(
    (v) => v.value && typeof v.value.rangeStart === 'number',
  );
}

/**
 * Build an InputExpr::Column from a quality field name.
 *
 * @param {string} name - column name in the matrix
 * @returns {Object} InputExpr with kind: "column"
 */
export function columnInput(name) {
  return { kind: 'column', name };
}

/**
 * Build an InputExpr::Derived wrapping another DerivedFieldExpr.
 *
 * @param {Object} spec - a DerivedFieldExpr
 * @returns {Object} InputExpr with kind: "derived"
 */
export function derivedInput(spec) {
  return { kind: 'derived', spec };
}

/**
 * Build a DerivedFieldExpr for a decile computation.
 *
 * @param {string} fieldName - output column name
 * @param {Object} input - an InputExpr (aggregation, column, or derived)
 * @returns {Object} DerivedFieldExpr
 */
export function decileExpr(fieldName, input) {
  return {
    fieldName,
    inputs: [input],
    function: {
      name: 'decile',
      config: { levels: 10 },
      output: { type: 'integer' },
    },
  };
}

/**
 * Build a DerivedFieldExpr for a percent-of-total computation.
 *
 * @param {string} fieldName - output column name
 * @param {Object} input - an InputExpr (aggregation, column, or derived)
 * @returns {Object} DerivedFieldExpr
 */
export function percentOfTotalExpr(fieldName, input) {
  return {
    fieldName,
    inputs: [input],
    function: {
      name: 'percentOfTotal',
      config: {},
      output: { type: 'number' },
    },
  };
}

/**
 * Build a DerivedFieldExpr for a propensity score computation.
 *
 * Unlike decile/percentOfTotal, propensity takes MULTIPLE inputs:
 * first input = binary target, rest = predictors.
 *
 * @param {string} fieldName - output column name
 * @param {Array<Object>} inputs - array of InputExpr
 * @param {Object} config - optional config (binCount, binScores, etc.)
 * @returns {Object} DerivedFieldExpr
 */
export function propensityExpr(fieldName, inputs, config = {}) {
  return {
    fieldName,
    inputs,
    function: {
      name: 'propensity',
      config: { binCount: 5, binScores: true, ...config },
      output: { type: 'number' },
    },
  };
}

/**
 * Map of function id → expr builder.
 *
 * fmap functions (arity 1): builder(fieldName, singleInput) → DerivedFieldExpr
 * apply functions (arity N): builder(fieldName, arrayOfInputs) → DerivedFieldExpr
 */
export const exprBuilders = {
  decile: decileExpr,
  percentOfTotal: percentOfTotalExpr,
  propensity: propensityExpr,
};

/**
 * Whether a function uses fmap (one expr per input) or apply (all inputs combined).
 */
export const functionModes = {
  decile: 'fmap',
  percentOfTotal: 'fmap',
  propensity: 'apply',
};

/**
 * Build DerivedFieldExpr(s) from a derived field group node.
 *
 * The node (height-3) has:
 *   - data.identifier: function id (e.g., "decile")
 *   - children: height-4 nodes (measurement inputs)
 *
 * For each child measurement, expands across series components:
 * if a payer component is in series mode with [MEDICAID, TOTAL THIRD PARTY],
 * produces one derived field per payer value.
 *
 * @param {Object} node - height-3 derived field tree node
 * @returns {Array<Object>} array of DerivedFieldExpr
 */
export function buildDerivedExprsFromNode(node) {
  const fnId = node.data?.identifier;
  const builder = exprBuilders[fnId];
  const mode = functionModes[fnId] || 'fmap';

  if (!builder) {
    console.warn(`No expr builder for derived field: ${fnId}`);
    return [];
  }

  // Collect all inputs from children
  const allInputs = collectInputsFromChildren(node.children, fnId);

  if (mode === 'apply') {
    // Apply mode: all inputs combined into a single expr (e.g., propensity)
    if (allInputs.length === 0) return [];

    const labels = allInputs.map((i) => i.label);
    const fieldName = `${labels.join('+')}.derivedField::${fnId}`;
    return [builder(fieldName, allInputs.map((i) => i.input))];
  }

  // Fmap mode: one expr per input (e.g., decile, percentOfTotal)
  return allInputs.map(({ input, label }) => {
    const fieldName = `${label}.derivedField::${fnId}`;
    return builder(fieldName, input);
  });
}

/**
 * Collect InputExpr objects from a list of child nodes.
 * Handles both measurement children and nested derivedFieldInput children.
 *
 * @param {Array} children - level-4 child nodes
 * @param {string} outerFnId - the outer function id (for nested labeling)
 * @returns {Array<{input: Object, label: string}>}
 */
function collectInputsFromChildren(children, outerFnId) {
  return (children || []).flatMap((child) => {
    // Level-4 nested derived field input
    if (child.data?.displayType === 'derivedFieldInput') {
      const { nestedSourceTree } = child.data;
      if (!nestedSourceTree?.groupData?.identifier) return [];

      const innerFnId = nestedSourceTree.groupData.identifier;
      const innerBuilder = exprBuilders[innerFnId];
      if (!innerBuilder) return [];

      const innerChildren = nestedSourceTree.children || [];
      const innerMode = functionModes[innerFnId] || 'fmap';

      const innerInputs = innerChildren
        .filter((c) => c?.value?.measurementType)
        .flatMap((c) => {
          const expanded = aggregationInputsFromNode(c.value);
          return expanded.map(({ input: innerInput, label: innerLabel }) => ({
            input: innerInput,
            label: innerLabel,
          }));
        });

      if (innerMode === 'apply') {
        // Inner function is apply: build one inner spec with all inputs
        const innerFieldName = innerInputs.map((i) => i.label).join('+') + `.derivedField::${innerFnId}`;
        const innerSpec = innerBuilder(innerFieldName, innerInputs.map((i) => i.input));
        return [{ input: derivedInput(innerSpec), label: innerFieldName }];
      }

      // Inner function is fmap: one inner spec per input
      return innerInputs.map(({ input: innerInput, label: innerLabel }) => {
        const innerFieldName = `${innerLabel}.derivedField::${innerFnId}`;
        const innerSpec = innerBuilder(innerFieldName, innerInput);
        return { input: derivedInput(innerSpec), label: innerFieldName };
      });
    }

    // Level-4 measurement input
    if (child.data?.value?.measurementType) {
      const expanded = aggregationInputsFromNode(child.data.value);
      return expanded.map(({ input, label }) => ({ input, label }));
    }

    // Level-4 quality column (for propensity predictors)
    if (child.data?.value?.qualityName) {
      const name = child.data.value.qualityName;
      return [{ input: columnInput(name), label: name }];
    }

    return [];
  });
}
